import { serverURL } from "@/utils/serverURL";
import type { SubstanceLensInfo } from "@/utils/types/substance";

// ── POST shape ─────────────────────────────────────────────────────────────

export type AutoFormulateMode = "loss" | "cost" | "prompt";

export type StartAutoFormulateBody = {
  mode: AutoFormulateMode;
  prompt?: string;
};

export type StartAutoFormulateResponse = { jobId: string };

export class AutoFormulateApiError extends Error {
  status: number;
  code: string | null;
  constructor(status: number, code: string | null, message: string) {
    super(message);
    this.name = "AutoFormulateApiError";
    this.status = status;
    this.code = code;
  }
}

export async function startAutoFormulate(
  flavorId: string,
  formulaId: number,
  versionId: number,
  body: StartAutoFormulateBody,
  signal?: AbortSignal,
): Promise<StartAutoFormulateResponse> {
  const res = await fetch(
    serverURL`/me/flavor/${flavorId}/formulas/${formulaId}/versions/${versionId}/auto-formulate`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    },
  );
  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* tolerate empty body */
  }
  if (!res.ok) {
    const code =
      payload && typeof payload === "object" && "error" in payload
        ? String((payload as { error: unknown }).error)
        : null;
    throw new AutoFormulateApiError(
      res.status,
      code,
      `startAutoFormulate failed: ${res.status} ${code ?? ""}`.trim(),
    );
  }
  return payload as StartAutoFormulateResponse;
}

// ── SSE event payloads — mirror server's wire types. ──────────────────────

export type AfInitialIngredient = {
  ndbTable: "public.compound" | "public.food";
  rowid: number;
  profile: number[] | null;
  /** Display name resolved server-side. Null only if the row is an
   *  orphan reference (deleted out-of-sync, etc.). Used by the client
   *  to register the ingredient in its runtime compound registry so
   *  row labels + narration strings resolve. */
  name?: string | null;
  /** Primary CAS — present on the RESULT event's newIngredientProfiles so the
   *  client can register full substance info on accept (no strip/flash).
   *  Absent on the initial event (those rows register via the formulas list). */
  cas?: string | null;
  /** Lens (family/chemClass/role/flavor labels) — present on the result event;
   *  absent on the initial event. */
  lens?: SubstanceLensInfo | null;
};

export type AfInitialFoodCompositionItem = {
  compoundId: number;
  profile: number[] | null;
};

export type AfInitialPayload = {
  jobId: string;
  embeddingDim: number;
  ingredients: AfInitialIngredient[];
  foodComposition: AfInitialFoodCompositionItem[];
};

export type AfProposedItem = {
  ndbTable: "public.compound" | "public.food";
  rowid: number;
  positionInSet: number;
  concentration: number | null;
  unit: string | null;
};

export type AfResultViz = {
  profile: number[] | null;
  drCoord: { x: number; y: number; z: number | null } | null;
  loss: number | null;
  tasteRadar: Record<string, number> | null;
  smellRadar: Record<string, number> | null;
};

export type AfStepMutationKind = "add" | "edit" | "remove";

export type AfStepMutation = {
  kind: AfStepMutationKind;
  ndbTable: "public.compound" | "public.food";
  rowid: number;
  /** Concentration before this step (null for adds). */
  prevPpm: number | null;
  /** Concentration after this step (null for removes). */
  newPpm: number | null;
  /** Unit before this step (null for adds). */
  prevUnit: string | null;
  /** Unit after this step (null for removes). */
  newUnit: string | null;
};

export type AfStep = {
  mutation: AfStepMutation;
  /** Viz state AFTER this mutation is applied. For edits and food
   *  mutations the viz equals the previous step's viz (concentration /
   *  foods don't enter mS). Last step's viz equals the top-level viz. */
  viz: AfResultViz;
};

export type AfResultPayload = {
  jobId: string;
  nblockResultId: number;
  proposedItems: AfProposedItem[];
  newIngredientProfiles: AfInitialIngredient[];
  viz: AfResultViz;
  /** Phase 6: server-defined sequence of intermediate {mutation, viz}
   *  pairs taking the formulation from initial → proposed. Client plays
   *  these in order during the `applying` phase so the Flavor Space dot
   *  and Radar plots reflect the real mS at each AI alteration. Empty
   *  when the proposed formulation is identical to the initial. Absent
   *  from older server builds — client falls back to its prior
   *  shuffle-and-interpolate path in that case. */
  steps?: AfStep[];
};

export type AfFailedPayload = { jobId: string; error: string };

// SSE URL helper. The server SSE endpoint is one-shot per click.
export function autoFormulateEventsUrl(
  flavorId: string,
  formulaId: number,
  versionId: number,
): string {
  return serverURL`/me/flavor/${flavorId}/formulas/${formulaId}/versions/${versionId}/auto-formulate/events`;
}

// Converts an SSE ingredient.ndbTable+rowid to the client's formulation key
// shape ("compound:<id>" | "food:<id>"). Used both in the initial-event
// handler and the result-event handler.
export function formulationKeyFromSse(
  ndbTable: "public.compound" | "public.food",
  rowid: number,
): string {
  const kind = ndbTable === "public.compound" ? "compound" : "food";
  return `${kind}:${rowid}`;
}
